import React, { createContext, ReactNode, useEffect, useRef, useState } from "react";
import { Users } from "../models/User.tsx";
import { onAuthStateChanged, signInAnonymously, signInWithCustomToken, signOut, User } from "firebase/auth";
import {
  doc,
  onSnapshot,
} from "firebase/firestore";
import { auth, db, functions } from "../services/Firebase.js";
import { UserIPData } from "../models/UserActivity.tsx";
import { AnyBet } from "../services/admin/BetsService.tsx";
import { clearAllCache } from "../helpers/queryCache.tsx";
import { clearWeeklyAccountViewSession } from "../helpers/weeklyFiguresSelectors.tsx";
import { invalidateHierarchyRoster } from "../helpers/hierarchyRoster.ts";
import { clearFirestoreSdkLocalStorage } from "../helpers/clearFirestoreSdkLocalStorage.ts";
import { applyLoginChrome } from "../helpers/loginChrome.ts";
import { httpsCallable } from "firebase/functions";
import { fetchUser, setUserIdentity } from "../services/player/LoginService.tsx";
import {
  clearAppBackgrounded,
  clearBrowserSessionAlive,
  hasBrowserSessionAlive,
  isSessionExpiredAck,
  isVoluntaryLogout,
  markBrowserSessionAlive,
  markSessionExpiredPending,
  markVoluntaryLogout,
} from "../helpers/browserSession.ts";

interface UserContextType {

  user: Users | null;
  updateUser: (newData: Partial<Users>) => void;
  logout: (options?: { voluntary?: boolean }) => Promise<void>;
  isLoading: boolean;
  userIpData?: UserIPData;
  setUserIpData: (data?: UserIPData) => void;
  selectedOption: string;
  setSelectedOption: React.Dispatch<React.SetStateAction<string>>;
  pendingBet: AnyBet[] | null;
  setPendingBet: React.Dispatch<React.SetStateAction<AnyBet[] | null>>;
  login: (username: string, pin: string) => Promise<Users | undefined>;
  error: Error | null;
  firebaseUser: User | null;

}
interface UserProviderProps {
  children: ReactNode;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

/** Sync flag on cold start so the guard/routes see expiry before Firebase restores. */
const flagDeadSessionOnBoot = () => {
  try {
    if (hasBrowserSessionAlive() || isVoluntaryLogout() || isSessionExpiredAck()) return;
    if (localStorage.getItem("user")) {
      markSessionExpiredPending();
    }
  } catch {
    // ignore
  }
};
flagDeadSessionOnBoot();

const UserProvider = ({ children }: UserProviderProps) => {

  const [user, setUser] = useState<Users | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userIpData, setUserIpData] = useState<UserIPData | undefined>();
  const [selectedOption, setSelectedOption] = useState('The Basics');
  const [pendingBet, setPendingBet] = useState<AnyBet[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const isLoggingIn = useRef(false);

  const persistUser = (userData: Users) => {
    const userToStore = { ...userData, token: undefined };
    localStorage.setItem("user", JSON.stringify(userToStore));
    setUser(userToStore);
  };

  const login = async (username: string, pin: string): Promise<Users | undefined> => {
    try {
      isLoggingIn.current = true;
      setIsLoading(true);
      const loginWithPin = httpsCallable(functions, 'loginWithPin');
      const result = await loginWithPin({ username, pin });
      const user = result.data as Users;

      if ("error" in user) {
        setError(new Error("Incorrect username or password"));
        setIsLoading(false);
        return;
      }

      if (user.status === "Disabled") {
        setError(new Error("Your account is deactivated."));
        setIsLoading(false);
        return;
      }

      const token = user.token;
      user.token = undefined;
      persistUser(user);
      // Mark alive before Firebase token work so SessionResumeGuard cannot
      // treat this mid-login persist as a dead tab session.
      markBrowserSessionAlive();

      if (!user?.uid) {
        const credential = await signInAnonymously(auth);
        await setUserIdentity(user.id!, credential?.user?.uid);
      } else if (token) {
        await signInWithCustomToken(auth, token);
      }
      setFirebaseUser(auth.currentUser);
      return user;

    } catch (error) {

      setError(error as Error);
      console.error("Login error:", error);

    } finally {

      isLoggingIn.current = false;
      setIsLoading(false);

    }

  };


  const loadUser = async (authUid?: string) => {
    try {
      const localUser = localStorage.getItem("user");

      if (localUser) {
        const cachedUser: Users = JSON.parse(localUser);
        const userData = await fetchUser({ docId: cachedUser.id });
        if (userData && typeof userData === "object" && userData.userName) {
          persistUser(userData);
          return userData;
        }
      }


      if (authUid) {
        const userData = await fetchUser({ uid: authUid });
        if (userData && typeof userData === "object" && userData.userName) {
          persistUser(userData);
          return userData;
        }

      }
      return null;

    } catch (error) {
      console.error("Error loading user:", error);
      return null;
    }

  };


  useEffect(() => {

    const unsub = onAuthStateChanged(auth, async (authUser) => {
      setFirebaseUser(authUser);
      setError(null);
      if (!authUser) {
        if (!isLoggingIn.current) {
          localStorage.removeItem("user");
          setUser(null);
        }
        setIsLoading(false);
        return;
      }
      if (isLoggingIn.current) {
        setIsLoading(false);
        return;
      }

      // Auth restored after browser/tab close (sessionStorage gone).
      // If the user already acknowledged the modal / is on login, do not re-flag.
      if (!hasBrowserSessionAlive() && !isVoluntaryLogout()) {
        if (!isSessionExpiredAck()) {
          markSessionExpiredPending();
        }
        localStorage.removeItem("user");
        setUser(null);
        setIsLoading(false);
        return;
      }

      const userData = await loadUser(authUser.uid);

      if (!userData?.userName) {
        setUser(null);
        setError(new Error("User profile not found for this authenticated session."));
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
    });

    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const updateUser = (newData: Partial<Users>) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      const updatedUser = {
        ...prevUser,
        ...newData,
        token: undefined,
      } as Users;
      localStorage.setItem("user", JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  useEffect(() => {
    const userId = user?.id;
    const userType = user?.userType;
    if (!userId || userType === "masterAgent" || userType === "subAgent") return;

    const userRef = doc(db, "users", userId);
    const unsub = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        setUser((prevUser) => {
          if (!prevUser || prevUser.id !== snap.id) return prevUser;
          const next = {
            ...prevUser,
            balance: data.balance ?? prevUser.balance,
            available: data.available ?? prevUser.available,
            freePlay: data.freePlay ?? prevUser.freePlay,
            creditLimit: data.creditLimit ?? prevUser.creditLimit,
            pending: data.pending ?? prevUser.pending,
            token: undefined,
          } as Users;
          if (
            next.balance === prevUser.balance &&
            next.available === prevUser.available &&
            next.freePlay === prevUser.freePlay &&
            next.creditLimit === prevUser.creditLimit &&
            next.pending === prevUser.pending
          ) {
            return prevUser;
          }
          localStorage.setItem("user", JSON.stringify(next));
          return next;
        });
      },
      (err) => {
        if (err.code === "permission-denied") return;
        console.error("Live user wallet listener failed:", err);
      },
    );

    return unsub;
  }, [user?.id, user?.userType]);

  useEffect(() => {
    const storedIp = localStorage.getItem('userIpData');
    if (storedIp) {
      setUserIpData(JSON.parse(storedIp));
    }
  }, []);

  const logout = async (options?: { voluntary?: boolean }) => {
    const voluntary = options?.voluntary !== false;

    try {
      // Keep Safari top chrome on brand navy before leaving the signed-in shell.
      applyLoginChrome();

      // Must run first on manual logout so SessionResumeGuard does not treat this as a dead browser session.
      if (voluntary) {
        markVoluntaryLogout();
      }
      setUser(null);
      setFirebaseUser(null);
      setUserIpData(undefined);
      localStorage.removeItem("lastActivity");
      localStorage.removeItem("userIpData");
      localStorage.removeItem("user");
      localStorage.removeItem("bannerSportConfig");
      clearAppBackgrounded();
      clearBrowserSessionAlive();
      clearWeeklyAccountViewSession();
      clearAllCache();
      invalidateHierarchyRoster();
      await signOut(auth);
      // SDK metadata only — does not clear Auth persistence used on refresh.
      clearFirestoreSdkLocalStorage();

    } catch (err) {
      console.error("Logout error:", err);
    }

  };

  const data = {
    user,
    updateUser,
    logout,
    isLoading,
    userIpData, setUserIpData,
    selectedOption, setSelectedOption,
    pendingBet, setPendingBet,
    login,
    error,
    firebaseUser
  };

  return (
    <UserContext.Provider value={data}>
      {children}
    </UserContext.Provider>
  )
}

export { UserProvider };
export default UserContext;

export const useUser = () => {
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within UserProvider");
  }
  return context;
};

